import {
  json,
  redirect,
} from "@remix-run/cloudflare";
import type {
  HeadersFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/cloudflare";
import {
  Link,
  useLoaderData,
} from "@remix-run/react";
import clsx from "clsx";
import { createSbClientSSR } from "~/utils/createSbClientSSR";
import { PostService } from "~/database/post.service";
import { generateHTML } from "@tiptap/html";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  generateFileURL,
  generateFileURLBasePath,
} from "~/utils/file/generateFileURL";
import { dateDisplay } from "~/utils/dateDisplay";
import PostItem from "~/components/Post/PostItem";
import UserIcon from "~/components/Common/UserIcon";
import PostActionList from "~/components/Post/PostActionList";
import {
  COMMENT_PER_PAGE,
  DEFAULT_DESCRIPTION,
  TITLE_SUFFIX,
} from "~/utils/const";
import {
  environment,
  environmentBrowsor,
} from "~/utils/env";
import { EditerTiptap } from "~/components/Editor/Tiptap";
import { convertName } from "~/utils/convertName";
import { CommentService } from "~/database/comment.service";
import { CommentList } from "./CommentList";
import { StampService } from "~/database/stamp.service";
import StampList from "~/components/Post/StampList";
import PostBottomSticky from "./PostBottomSticky";
import { useGetThum } from "~/utils/dom/useGetThum";
import { useAuthUser } from "~/utils/hook/useIsLogin";

export const meta: MetaFunction<
  typeof loader
> = ({ data }) => {
  return [
    { title: data?.post.title + TITLE_SUFFIX },
    {
      name: "description",
      content: DEFAULT_DESCRIPTION,
    },
    {
      property: "og:title",
      content: data?.post.title + TITLE_SUFFIX,
    },
    {
      property: "og:description",
      content: DEFAULT_DESCRIPTION,
    },
    {
      property: "og:type",
      content: "article",
    },
    {
      property: "og:url",
      content:
        data?.basePath +
        "/posts/" +
        data?.post.display_id,
    },
    {
      property: "og:image",
      content:
        generateFileURL(
          data?.post.thumbnail_path ?? "",
          undefined,
          data?.basePath,
        ) ?? "",
    },
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
  ];
};

export const headers: HeadersFunction = ({
  loaderHeaders,
}) => ({
  "op-cache-status":
    loaderHeaders.get("op-cache-status") ??
    "MISS",
});

export const loader = async ({
  context,
  request,
  params,
}: LoaderFunctionArgs) => {
  const headers = new Headers();
  const supabase = createSbClientSSR(
    headers,
    request,
    context,
  );
  let postId = params.postId as string;
  const url = new URL(request.url);
  const searchParams = url.searchParams;
  const isDraft = searchParams.get(
    "draft",
  ) as string;
  try {
    const getFileURL = (
      content_path: string | null,
    ) => {
      const fileURL = generateFileURL(
        content_path!,
        context,
      );
      return fileURL;
    };

    const post =
      isDraft === "true"
        ? await PostService.getPostSingleWithContent(
            supabase,
            postId,
            context,
          )
        : await PostService.getPostSinglePublicWithContent(
            supabase,
            postId,
            getFileURL,
          );

    if (
      postId === post.id &&
      post.id !== post.display_id
    ) {
      return redirect(
        `/${post.user.display_id}/posts/${post.display_id}`,
        301,
      );
    }

    postId = post.id; // in case of display_id used
    const reqURL = new URL(request.url);
    const pathname = reqURL.pathname;
    const reqURLSplit = pathname.split("/");

    if (reqURLSplit[1] !== post.user.display_id) {
      return redirect(
        `/${post.user.display_id}/posts/${post.display_id}`,
        301,
      );
    }

    const userPosts =
      await PostService.getUserPosts(
        supabase,
        post.user_id,
        {
          limit: 3,
          exclude: [post.id],
        },
      );
    const commentCursor =
      await CommentService.getCommentByPosts(
        supabase,
        postId,
        undefined,
        {
          limit: COMMENT_PER_PAGE,
        },
      );

    const stamps =
      await StampService.getStampsWithCount(
        supabase,
        postId,
      );
    const env = environment(context);
    const basePath = generateFileURLBasePath({
      ...env,
      DB_ENV: env.DB_ENV ?? "local",
    });
    return json(
      {
        commentCursor,
        post: post,
        userPosts,
        stamps,
        basePath,
      },

      {
        headers: headers,
      },
    );
  } catch (_e) {
    const e = _e as Error;
    console.log("e:", e);
    if (e.message === "Post not found") {
      // 404
      throw new Response(null, {
        status: 404,
        statusText: "Not Found",
      });
    } else if (
      e.message === "DB Access Error" ||
      e.message === "VAlidationError"
    ) {
      // 500
      throw new Response(null, {
        status: 500,
        statusText: "Server Error",
      });
    } else {
      throw new Response(null, {
        status: 500,
        statusText: "Server Error Unknown",
      });
    }
  }
};

export default function Index() {
  const { post, userPosts, stamps } =
    useLoaderData<typeof loader>();

  const output = useMemo(() => {
    return post.content
      ? generateHTML(JSON.parse(post.content), [
          ...EditerTiptap,
        ])
      : "";
  }, [post.content]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const envB = environmentBrowsor();
      const BASE_URL =
        envB.WORKER_URL + "/posts/v/" + post.id;
      // BASE_URLに対してPOST
      fetch(BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        // no-cors
        mode: "no-cors",
      });
    }
  }, [post.id]);

  const [bottomInview, setBottomInview] =
    useState(false);
  const onScroll = useCallback(() => {
    if (window.scrollY < window.innerHeight / 2) {
      setBottomInview(false);
    } else {
      setBottomInview(true);
    }
  }, []);
  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener(
        "scroll",
        onScroll,
      );
    };
  }, [onScroll]);
  const getThum = useGetThum();

  const { user } = useAuthUser();
  return (
    <div
      className={clsx(
        "pb-128 pt-80 text-black",
        "max-md:pt-90",
        "max-md:pb-0",
      )}
    >
      <div className={clsx()}>
        {user?.id === post.user_id && (
          <div
            className={clsx(
              "sticky",
              "right-0",
              "top-80",
              "z-10",
              "w-full",
              "mb-30",
            )}
          >
            <Link
              to={`/posts/edit/${post.id}`}
              className={clsx(
                "px-16",
                "py-6",
                "text-12",
                "block",
                "bg-gray-400",
                "text-center",
                "text-white",
                "hover:bg-gray-300",
              )}
            >
              この記事を編集
            </Link>
          </div>
        )}
        <div
          className={clsx(
            "mx-auto",
            "w-1305",
            "grid",
            "grid-cols-[1fr_auto]",
            "gap-80",
            "max-md:w-360",
            "max-md:block",
            "z-[1]",
          )}
        >
          <div>
            <h1
              className={clsx(
                "font-bold",
                "text-25",
              )}
            >
              {post.title}
            </h1>
            <div className={clsx("mt-10")}>
              <StampList
                stamps={stamps}
                postId={post.id}
              ></StampList>
            </div>
            <div
              className={clsx(
                "flex",
                "items-center",
                "text-12",
                "text-gray-400",
                "mt-8",
                "max-md:flex-wrap",
              )}
            >
              <div className={clsx("font-bold")}>
                <Link
                  to={`/user/${post.user_id}/`}
                >
                  {convertName(post.user.name)}
                </Link>
              </div>
              <div
                className={clsx(
                  "flex",
                  "border-l",
                  "ml-10",
                )}
              >
                {post.categories?.map((el) => {
                  return (
                    <div
                      className={clsx(
                        "pl-0",
                        "ml-8",
                      )}
                      key={el.id}
                    >
                      <Link
                        to={`/categories/${el.id}`}
                      >
                        {el.name}
                      </Link>
                    </div>
                  );
                })}
              </div>
              <div
                className={clsx(
                  "pl-8",
                  "ml-8",
                  "border-l",
                )}
              >
                {dateDisplay(post.created_at)}
              </div>
            </div>

            {/*      {post.overview && (
              <div
                className={clsx(
                  "text-14",
                  "mt-10",
                  "max-md:text-13",
                  "whitespace-pre-wrap",
                )}
              >
                {post.overview}
              </div>
            )}
 */}
            <div
              className={clsx(
                "mt-10",
                "flex",
                "justify-end",
                "max-md:mt-14",
              )}
            >
              <PostActionList
                post={post}
              ></PostActionList>
            </div>

            <div>
              {post.thumbnail_path && (
                <div
                  className={clsx(
                    "aspect-video",
                    "mt-25",
                  )}
                >
                  <img
                    src={getThum(
                      post.thumbnail_path,
                      { width: 1500 },
                    )}
                    className={clsx(
                      "h-full",
                      "w-full",
                      "object-cover",
                    )}
                    alt=""
                  />
                </div>
              )}
            </div>

            <div className="my-48 min-h-[100vh]">
              <div className="articleContent">
                <div
                  dangerouslySetInnerHTML={{
                    __html: output,
                  }}
                />
              </div>
            </div>
          </div>

          <div
            className={clsx("w-288", "pc_only")}
          >
            <div
              className={clsx("w-154", "mx-auto")}
            >
              <Link to={`/user/${post.user_id}`}>
                <UserIcon
                  iconPath={
                    post.user.iconurl ?? ""
                  }
                />
              </Link>
            </div>
            <div
              className={clsx(
                "text-center",
                "mt-16",
                "text-lg",
                "font-bold",
              )}
            >
              {convertName(post.user.name)}
            </div>
            <div
              className={clsx(
                "flex items-center justify-center",
                "gap-20",
              )}
            >
              <div
                className={clsx(
                  "flex items-center justify-center",
                  "font-bold",
                  "text-20",
                  "gap-4",
                )}
              >
                {post.user.public_posts_count}
                <div
                  className={clsx(
                    "text-gray-400",
                    "font-normal",
                    "text-13",
                  )}
                >
                  プレスリリース
                </div>
              </div>
              {/*   <div
                className={clsx(
                  "flex items-center justify-center",
                  "font-bold",
                  "text-20",
                  "gap-4",
                )}
              >
                {post.user.posts_likes_count}
                <div
                  className={clsx(
                    "text-gray-400",
                    "font-normal",
                    "text-13",
                  )}
                >
                  ブックマーク
                </div>
              </div> */}
            </div>
            <div
              className={clsx(
                "text-15",
                "mt-8",
                "font-normal",
              )}
            >
              {post.user.profile}
            </div>

            {userPosts.length > 0 && (
              <div className={clsx("mt-32")}>
                <div
                  className={clsx(
                    "grid",
                    "grid-cols-[auto_1fr]",
                    "gap-16",
                    "items-center",
                  )}
                >
                  <h3
                    className={clsx(
                      "font-regular",
                    )}
                  >
                    Other Posts
                  </h3>
                  <div
                    className={clsx("border-t-2")}
                  ></div>
                </div>
                <div
                  className={clsx(
                    "flex",
                    "flex-col",
                    "gap-16",
                    "mt-24",
                  )}
                >
                  {userPosts.map((el, i) => {
                    return (
                      <li
                        key={i}
                        className={clsx("")}
                      >
                        <PostItem
                          data={el}
                        ></PostItem>
                      </li>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <PostBottomSticky
          bottomInview={bottomInview}
        ></PostBottomSticky>
      </div>

      <div
        className={clsx(
          "mt-10",
          "max-md:mt-25",
          "w-1305",
          "max-md:w-360",
          "mx-auto",
        )}
      >
        <StampList
          stamps={stamps}
          postId={post.id}
        ></StampList>
      </div>
      <CommentList></CommentList>
    </div>
  );
}
